import type { FileModel, IApiConfig, SearchFilesResponse } from "./api";

export type CoreResourceSearchRequest = {
  attributes?: Readonly<Record<string, string>>;
  pageIndex?: number;
  pageSize?: number;
  sortBy?: string;
  sortAscending?: boolean;
  publicOnly?: boolean;
};
export type CoreFileOptions = { signal?: AbortSignal };
export type CoreUploadOptions = CoreFileOptions & { attributeKey?: string; attributeValue?: string; publishPublicly?: boolean };
export type CoreFileModel = FileModel & { publicUrl?: string };
export type FilePickerCapabilities = Readonly<{ canSelect: boolean; canUpload: boolean; maximumFileBytes: number; acceptedContentTypes: readonly string[]; canPublishPublicly?: boolean; canFilterPublic?: boolean }>;

export class ReactCoreClient {
  private readonly http: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> };

  constructor(
    private readonly configuration: IApiConfig,
    private readonly baseUrl = "",
    http?: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> }
  ) {
    this.http = http ?? window;
  }

  forHost(definitionId: string, componentId: string) {
    const root = `/Core/${encodeURIComponent(definitionId)}/${encodeURIComponent(componentId)}`;
    const idPath = (kind: string, id: string) => `${root}/${kind}/${encodeURIComponent(id)}`;
    const search = (request: CoreResourceSearchRequest = {}) => ({
      ...request,
      attributes: request.attributes ?? {},
      pageIndex: request.pageIndex ?? 0,
      pageSize: request.pageSize ?? 20,
    });
    const form = (file: File, options?: CoreUploadOptions) => {
      const body = new FormData();
      body.append("File", file, file.name);
      if (options?.attributeKey) body.append("AttributeKey", options.attributeKey);
      if (options?.attributeValue !== undefined) body.append("AttributeValue", options.attributeValue);
      if (options?.publishPublicly) body.append("PublishPublicly", "true");
      return body;
    };
    return {
      files: {
        capabilities: (options?: CoreFileOptions): Promise<FilePickerCapabilities> => this.json(`${root}/Files/Capabilities`, "GET", undefined, options),
        search: (request?: CoreResourceSearchRequest, options?: CoreFileOptions): Promise<Omit<SearchFilesResponse, "items"> & { items: CoreFileModel[] }> => this.json(`${root}/Files/Search`, "POST", search(request), options),
        getById: (id: string, options?: CoreFileOptions): Promise<CoreFileModel> => this.json(idPath("Files", id), "GET", undefined, options),
        upload: (file: File, options?: CoreUploadOptions): Promise<{ id: string; publicUrl?: string }> =>
          this.request(`${root}/Files`, { method: "POST", body: form(file, options), signal: options?.signal }),
        rename: (id: string, name: string): Promise<void> => this.json(`${idPath("Files", id)}/Name`, "PUT", { name }),
        updateContent: (id: string, file: File): Promise<void> => this.request(`${idPath("Files", id)}/Content`, { method: "PUT", body: form(file) }),
        download: (id: string): Promise<Blob> => this.request(`${idPath("Files", id)}/Content`, { method: "GET" }, true),
        delete: (id: string): Promise<void> => this.json(idPath("Files", id), "DELETE"),
      },
    };
  }

  private json<T>(path: string, method: string, body?: unknown, options?: CoreFileOptions): Promise<T> {
    return this.request(path, { method, signal: options?.signal, ...(body === undefined ? {} : { body: JSON.stringify(body), headers: { "Content-Type": "application/json" } }) });
  }

  private async request<T>(path: string, init: RequestInit, binary = false): Promise<T> {
    const token = await this.configuration.getAccessToken();
    const response = await this.http.fetch(`${this.baseUrl}${path}`, {
      ...init,
      credentials: "include",
      cache: "no-store",
      headers: { ...init.headers, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!response.ok) {
      const detail = await response.text();
      throw new Error(detail || `Core service request failed (${response.status}).`);
    }
    if (response.status === 204) return undefined as T;
    return (binary ? await response.blob() : await response.json()) as T;
  }
}
